import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User, Bot, ArrowRight, Building2, Users, Globe, MapPin, Lock, Clock, Phone, Mail, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { workspaceProducts } from '../data/workspaceData';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMenu, setCurrentMenu] = useState('main');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [humanContactForm, setHumanContactForm] = useState({ name: '', email: '', phone: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial welcome message with direct services menu
      setTimeout(() => {
        addBotMessage("Hello! 👋 Welcome to Laksh Space. I'm here to help you find the perfect workspace solution. Please select a service category:", 'services-menu');
      }, 500);
    }
  }, [isOpen]);

  const addBotMessage = (text, menuType = null, data = null) => {
    const message = {
      id: Date.now(),
      type: 'bot',
      text,
      menuType,
      data,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
    if (menuType) {
      setCurrentMenu(menuType);
    }
  };

  const addUserMessage = (text) => {
    const message = {
      id: Date.now(),
      type: 'user',
      text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
  };

  const handleMenuClick = (option, value) => {
    addUserMessage(option);
    
    switch (value) {
      case 'back-to-services':
        addBotMessage("Which service category interests you?", 'services-menu');
        break;
        
      case 'contact':
        addBotMessage("Here's how you can reach us:", 'contact-info');
        break;
        
      case 'talk-to-human':
        addBotMessage("I'll connect you with our team. Please choose your preferred contact method:", 'human-contact');
        break;
        
      default:
        // Handle service category selection
        if (workspaceProducts[value]) {
          setSelectedCategory(value);
          const category = workspaceProducts[value];
          addBotMessage(`Excellent choice! Here are our ${category.title} options:`, 'category-products', category);
        }
        break;
    }
  };

  const getIconForCategory = (categoryId) => {
    const iconMap = {
      'office-space': Building2,
      'coworking': Users,
      'virtual-office': Globe,
      'meeting-rooms': Users,
      'business-addresses': MapPin,
      'private-offices': Lock,
      'day-offices': Clock
    };
    return iconMap[categoryId] || Building2;
  };

  const MainMenu = () => (
    <div className="space-y-2 mt-3">
      <button
        onClick={() => handleMenuClick("Browse Services", 'services')}
        className="w-full text-left p-3 bg-[#e30613] text-white rounded-lg hover:bg-[#c1050f] transition-colors flex items-center justify-between"
      >
        <span>🏢 Browse Services</span>
        <ArrowRight size={16} />
      </button>
      <button
        onClick={() => handleMenuClick("Get Pricing Info", 'pricing')}
        className="w-full text-left p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-between"
      >
        <span>💰 Get Pricing Info</span>
        <ArrowRight size={16} />
      </button>
      <button
        onClick={() => handleMenuClick("Book a Space", 'booking')}
        className="w-full text-left p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-between"
      >
        <span>📅 Book a Space</span>
        <ArrowRight size={16} />
      </button>
      <button
        onClick={() => handleMenuClick("Contact Information", 'contact')}
        className="w-full text-left p-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-between"
      >
        <span>📞 Contact Information</span>
        <ArrowRight size={16} />
      </button>
      <button
        onClick={() => handleMenuClick("Talk to Human", 'talk-to-human')}
        className="w-full text-left p-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-between"
      >
        <span>👤 Talk to Human</span>
        <ArrowRight size={16} />
      </button>
    </div>
  );

  const ServicesMenu = () => (
    <div className="space-y-2 mt-3">
      {/* {Object.entries(workspaceProducts).map(([key, category]) => {
        const IconComponent = getIconForCategory(key);
        return (
          <button
            key={key}
            onClick={() => handleMenuClick(category.title, key)}
            className="w-full text-left p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-between"
          >
            <div className="flex items-center space-x-2">
              <IconComponent size={16} className="text-[#e30613]" />
              <span className="font-medium">{category.title}</span>
            </div>
            <ArrowRight size={16} />
          </button>
        );
      })} */}
      <button
        onClick={() => handleMenuClick("Need Help? Talk to Human", 'talk-to-human')}
        className="w-full text-left p-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-between"
      >
        <span>👤 Need Help? Talk to Human</span>
        <ArrowRight size={16} />
      </button>
    </div>
  );

  const CategoryProducts = ({ category }) => (
    <div className="space-y-3 mt-3">
      {category.products.map((product, index) => (
        <div key={product.id} className="bg-gray-50 rounded-lg p-3 border">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">{product.name}</h4>
            <span className="text-[#e30613] font-bold text-sm">{product.pricing}</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">{product.description}</p>
          <div className="flex justify-between items-center">
            <span className="text-xs text-green-600 font-medium">{product.availability}</span>
            <Link
              to={`/${category.id}`}
              className="bg-[#e30613] text-white px-3 py-1 rounded text-xs hover:bg-[#c1050f] transition-colors"
            >
              View Details
            </Link>
          </div>
        </div>
      ))}
      <button
        onClick={() => handleMenuClick("← Back to Services", 'back-to-services')}
        className="w-full text-left p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors mt-3"
      >
        ← Back to Services
      </button>
    </div>
  );

  const ContactInfo = () => (
    <div className="space-y-3 mt-3">
      <div className="bg-gray-50 rounded-lg p-3">
        <h4 className="font-semibold mb-2 flex items-center">
          <Phone size={16} className="mr-2 text-[#e30613]" />
          Phone Numbers
        </h4>
        <div className="space-y-1">
          <a href="tel:+919972235566" className="block text-blue-600 hover:underline">
            +91 99722 35566
          </a>
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-3">
        <h4 className="font-semibold mb-2 flex items-center">
          <Mail size={16} className="mr-2 text-[#e30613]" />
          Quick Actions
        </h4>
        <div className="space-y-2">
          <Link
            to="/contact"
            className="block bg-[#e30613] text-white text-center py-2 rounded hover:bg-[#c1050f] transition-colors"
          >
            Visit Contact Page
          </Link>
          <Link
            to="/gallery"
            className="block bg-blue-500 text-white text-center py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Book a Tour
          </Link>
        </div>
      </div>
      
      <button
        onClick={() => handleMenuClick("← Back to Services", 'back-to-services')}
        className="w-full text-left p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
      >
        ← Back to Services
      </button>
    </div>
  );

  const HumanContact = () => {
    const handleFormChange = (e) => {
      const { name, value } = e.target;
      setHumanContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = async (e) => {
      e.preventDefault();
      
      if (!humanContactForm.name || !humanContactForm.email || !humanContactForm.phone) {
        alert('Please fill in all fields');
        return;
      }

      try {
        await fetch("https://script.google.com/macros/s/AKfycbyl2RCmSBzMD2UJmYKxTw-TbDAxLVPFuJm2wO-5kQBE1mP3ij1DfNt9kRNc_7_oWCuIGQ/exec", {
          method: "POST",
          body: JSON.stringify({
            name: humanContactForm.name,
            email: humanContactForm.email,
            phone: humanContactForm.phone,
            message: 'Talk to Human Request'
          }),
        });

        addBotMessage("Thank you! We've received your information. Our team will contact you shortly.", 'main-menu');
        setHumanContactForm({ name: '', email: '', phone: '' });
      } catch (error) {
        addBotMessage("There was an error submitting your information. Please try again.", 'main-menu');
      }
    };

    return (
      <div className="space-y-3 mt-3">
        <form onSubmit={handleFormSubmit} className="space-y-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={humanContactForm.name}
              onChange={handleFormChange}
              placeholder="Your name"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#e30613]"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={humanContactForm.email}
              onChange={handleFormChange}
              placeholder="your@email.com"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#e30613]"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              name="phone"
              value={humanContactForm.phone}
              onChange={handleFormChange}
              placeholder="+91 XXXXXXXXXX"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#e30613]"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#e30613] text-white p-2 rounded font-semibold hover:bg-[#c1050f] transition-colors text-sm"
          >
            Submit
          </button>
        </form>
        <button
          onClick={() => {
            handleMenuClick("← Back to Services", 'back-to-services');
            setHumanContactForm({ name: '', email: '', phone: '' });
          }}
          className="w-full text-left p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
        >
          ← Back to Services
        </button>
      </div>
    );
  };

  const renderMenu = (menuType, data = null) => {
    switch (menuType) {
      case 'main-menu':
        return <MainMenu />;
      case 'services-menu':
        return <ServicesMenu />;
      case 'category-products':
        return <CategoryProducts category={data} />;
      case 'contact-info':
        return <ContactInfo />;
      case 'human-contact':
        return <HumanContact />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Chat Bot Trigger Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
            isOpen
              ? 'bg-gray-600 hover:bg-gray-700'
              : ""
          }`}
        >
          {isOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <img src="./images-optimized/galery-images/Icon for Virtual.webp"  alt="Virtual Assistant" className="rounded-xl w-12 h-12" />
          )}
        </button>
        
        {/* {!isOpen && (
          <div className="absolute -top-12 left-0 bg-black text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap">
            Hey I am your virtual assistant
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
          </div>
        )} */}
      </div>

      {/* Chat Bot Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 h-96 bg-white rounded-lg shadow-2xl border z-50 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#e30613] to-[#c1050f] text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img src="./images-optimized/galery-images/Icon for Virtual.webp" alt="Virtual Assistant" className="w-5 h-5" />
                <div>
                  <h3 className="font-semibold">Virtual Assistant</h3>
                  <p className="text-xs opacity-90">Online • Ready to help</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    message.type === 'user'
                      ? 'bg-[#e30613] text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {message.type === 'bot' && (
                      <img src="./images-optimized/galery-images/Icon for Virtual.webp" alt="Virtual Assistant" className="w-8 h-8 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm">{message.text}</p>
                      {message.menuType && renderMenu(message.menuType, message.data)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50 rounded-b-lg">
            <p className="text-xs text-gray-500 text-center">
              Powered by Laksh Space • Always here to help
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;